/* global SideDrawer */

if (!customElements.get('quick-add-drawer')) {
  // The product block of a product page: the main product section's own element (its data-section is the section id).
  // Every .js-quick-add opener (snippets/product-card.liquid, snippets/product-card-mini.liquid, any new one) must print
  // data-template-suffix: Shopify answers ?section_id=<another template's main section> with 200 and the requested id, so the id
  // check below cannot catch an opener that names the wrong template.
  const PRODUCT_BLOCK = /<div class="product js-product[^"]*" data-section="([^"]+)"/;

  // The Section Rendering API answers 200 with an empty product for a URL that no longer resolves, so the content is checked
  // too: a product block without a title is a failed read (a whole page of a gone product is a 404 and fails on its own).
  const hasProduct = (html) => {
    const tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    const title = tmpl.content.querySelector('.cc-main-product .js-product .product-title');
    return !!title && title.textContent.trim() !== '';
  };

  class QuickAddDrawer extends SideDrawer {
    constructor() {
      super();
      this.content = this.querySelector('.js-product-details');
      this.footer = this.querySelector('.drawer__footer');
      this.form = this.querySelector('product-form');
      this.oversizeContainer = this.querySelector('#oversize-quick-add');
      this.notification = this.querySelector('.js-added-to-cart');
      this.backBtn = this.querySelector('.drawer__back-btn');
      this.openCartDrawerLinks = this.querySelectorAll('.js-open-cart-drawer');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.fetch = null;
      this.fetchedUrls = [];
      // Section id of the main product section per product template suffix ('default' = no suffix), learned from the
      // first full page of each template and kept for the visit. A known id lets the next quick add request only that section.
      this.sectionIds = {};
      this.learning = {};
      this.sectionIdsKey = 'umbra-qa-sections-' + (window.Shopify && window.Shopify.theme ? window.Shopify.theme.id : '');
      this.loadSectionIds();
      this.quickAddButtonMouseEnterHandler = this.handleQuickAddButtonMouseEnter.bind(this);
      this.documentClickHandler = this.handleDocumentClick.bind(this);

      document.addEventListener('click', this.documentClickHandler);
      this.addEventListener('on:variant:change', this.handleVariantChange.bind(this));

      this.openCartDrawerLinks.forEach((link) => {
        link.addEventListener('click', this.handleOpenCartClick.bind(this));
      });

      if (theme.device.hasHover && theme.mediaMatches.md) {
        document.querySelectorAll('.js-quick-add').forEach((button) => {
          this.bindQuickAddButtonMouseEnter(button);
        });

        if ('MutationObserver' in window) {
          this.observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              // Add event listener to new buttons
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    this.bindQuickAddButtonMouseEnter(button);
                  });
                }
              });

              // Remove event listener from removed buttons
              mutation.removedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  node.querySelectorAll('.js-quick-add').forEach((button) => {
                    button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
                  });
                }
              });
            });
          });

          // Start observing changes to the DOM
          this.observer.observe(document.body, { childList: true, subtree: true });
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      document.querySelectorAll('.js-quick-add').forEach((button) => {
        button.removeEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      });
      if (this.observer) this.observer.disconnect();
    }

    /**
     * Bind a mouseenter event to a given quick add button if it's not already bound
     * @param {Element} button - The button element to bind the mouseenter event to.
     */
    bindQuickAddButtonMouseEnter(button) {
      if (!button.dataset.quickAddListenerAdded) {
        button.dataset.quickAddListenerAdded = 'true';
        button.addEventListener('mouseenter', this.quickAddButtonMouseEnterHandler);
      }
    }

    /**
     * Start the fetch on hover of a quick add button for perceived performance gains
     * @param {object} evt - Event object.
     */
    handleQuickAddButtonMouseEnter(evt) {
      if (!this.fetchedUrls.includes(evt.target.dataset.productUrl)) {
        const promise = this.fetchProductHtml(evt.target.dataset.productUrl, evt.target.dataset.templateSuffix);
        // The failure is handled when the drawer opens; this keeps an unused prefetch from logging an unhandled rejection.
        promise.catch(() => {});
        this.fetch = {
          url: evt.target.dataset.productUrl,
          promise
        };
        this.fetchedUrls.push(evt.target.dataset.productUrl);
      }
    }

    /**
     * Reads the learned section ids of this visit.
     */
    loadSectionIds() {
      try {
        const saved = JSON.parse(window.sessionStorage.getItem(this.sectionIdsKey) || '{}');
        Object.keys(saved || {}).forEach((key) => {
          if (typeof saved[key] === 'string') this.sectionIds[key] = saved[key];
        });
      } catch (e) {
        // Storage is not available: the ids are learned again from the next full page.
      }
    }

    /**
     * Keeps the learned section ids for the rest of the visit.
     */
    saveSectionIds() {
      try {
        window.sessionStorage.setItem(this.sectionIdsKey, JSON.stringify(this.sectionIds));
      } catch (e) {
        // Storage is not available: the ids stay in memory only.
      }
    }

    /**
     * Fetches a URL as text. A non-200 answer is an error.
     * @param {string} url - URL to fetch.
     * @returns {Promise<string>}
     */
    fetchText(url) {
      return fetch(url).then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      });
    }

    /**
     * Fetches a whole product page and remembers the id of its main product section for this template.
     * @param {string} url - Product URL.
     * @param {string} key - Template key ('default' or the template suffix).
     * @returns {Promise<string>}
     */
    fetchFullPage(url, key) {
      const request = this.fetchText(url).then((html) => {
        if (!hasProduct(html)) throw new Error('Product not found');
        const match = PRODUCT_BLOCK.exec(html);
        if (match) {
          this.sectionIds[key] = match[1];
          this.saveSectionIds();
        }
        return html;
      });

      // Other products of the same template wait for this answer instead of each fetching a full page.
      if (!this.learning[key] && !this.sectionIds[key]) {
        this.learning[key] = request;
        const done = () => {
          if (this.learning[key] === request) delete this.learning[key];
        };
        request.then(done, done);
      }

      return request;
    }

    /**
     * Fetches the markup of a product. When the id of the template's main product section is known only that section is
     * requested (Section Rendering API); otherwise, and after any error or an unexpected answer, the whole page.
     * @param {string} url - Product URL.
     * @param {string} [suffix] - Template suffix of the product (empty for the default template).
     * @returns {Promise<string>}
     */
    fetchProductHtml(url, suffix) {
      const key = suffix || 'default';
      const sectionId = this.sectionIds[key];

      if (sectionId) {
        const separator = url.indexOf('?') === -1 ? '?' : '&';
        let sectionAnswered = false;
        return this.fetchText(`${url}${separator}section_id=${encodeURIComponent(sectionId)}`)
          .then((html) => {
            const match = PRODUCT_BLOCK.exec(html);
            if (!match || match[1] !== sectionId) throw new Error('Unexpected section answer');
            // The section itself answered with its own id, so the id stays valid even when the product is gone.
            sectionAnswered = true;
            if (!hasProduct(html)) throw new Error('Product not found');
            return html;
          })
          .catch(() => {
            if (!sectionAnswered) {
              delete this.sectionIds[key];
              this.saveSectionIds();
            }
            return this.fetchFullPage(url, key);
          });
      }

      if (this.learning[key]) {
        return this.learning[key].then(
          () => (this.sectionIds[key] ? this.fetchProductHtml(url, suffix) : this.fetchFullPage(url, key)),
          () => this.fetchFullPage(url, key)
        );
      }

      return this.fetchFullPage(url, key);
    }

    /**
     * Handles 'click' events on success notification's 'View cart' link
     * @param {object} evt - Event object.
     */
    handleOpenCartClick(evt) {
      // Open the cart drawer if available on the page
      if (this.cartDrawer) {
        evt.preventDefault();
        this.cartDrawer.open();
      } else if (window.location.pathname === theme.routes.cart) {
        evt.preventDefault();
        this.close();
      }
    }

    /**
     * Handles 'click' events on the document.
     * @param {object} evt - Event object.
     */
    handleDocumentClick(evt) {
      if (!evt.target.matches('.js-quick-add')) return;

      // Close the cart drawer if it's open
      if (this.cartDrawer && this.cartDrawer.ariaHidden === 'false') {
        const overlay = document.querySelector('.js-overlay.is-visible');
        if (overlay) overlay.style.transitionDelay = '200ms';

        this.cartDrawer.close();

        // Wait a few ms for a more optimal ux/animation
        setTimeout(() => {
          this.backBtn.hidden = false;
          this.open(evt.target);
          if (overlay) overlay.style.transitionDelay = '';
        }, 200);
      } else {
        this.open(evt.target);
      }
    }

    /**
     * Handles 'on:variant:change' events on the Quick Add drawer.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      let url = this.productUrl;

      if (evt.detail.variant) {
        const separator = this.productUrl.split('?').length > 1 ? '&' : '?';
        url += `${separator}variant=${evt.detail.variant.id}`;
      }

      this.querySelectorAll('.js-prod-link').forEach((link) => {
        link.href = url;
      });

      // THE IMAGE. This was missing entirely: updateMedia() existed but was only ever called once,
      // when the drawer opened (see open(), "Update the product media"). Changing colour therefore
      // left the drawer showing the variant that happened to be selected on the card.
      // Mirrors the PDP's contract exactly - media-gallery.js:onVariantChange resolves
      // evt.detail.variant.featured_media.id against [data-media-id], and updateMedia() below
      // queries the same attribute.
      // Deliberately BEFORE the oversize block, because that block can throw (see below) and a
      // throw there would take the image update with it.
      if (evt.detail.variant && evt.detail.variant.featured_media) {
        this.updateMedia(evt.detail.variant.featured_media.id);
      }

      // OVERSIZE SURCHARGE. Every dereference below was previously unguarded, and each is reachable:
      //   - evt.detail.variant is null on an unavailable option combination. The code two lines up
      //     already tests for exactly that, then the old version read .id off it regardless.
      //   - a product with no variant-picker, or a drawer with no #oversize-callout, returns null.
      // A throw here is not cosmetic: it aborts the whole handler, so anything added after it
      // silently stops working too.
      if (!evt.detail.variant) return;

      const variantPicker = this.productEl && this.productEl.querySelector('variant-picker');
      const variantJson = variantPicker && variantPicker.querySelector('[type="application/json"]');
      const oversizeContainer = this.oversizeContainer
        && this.oversizeContainer.querySelector('#oversize-callout');
      if (!variantJson || !oversizeContainer) return;

      let oversize;
      try {
        const output = JSON.parse(variantJson.textContent);
        const entry = output.formatted && output.formatted[evt.detail.variant.id];
        oversize = entry && entry.oversize;
      } catch (e) {
        oversizeContainer.hidden = true;
        return;
      }

      const oversizeCharge = this.oversizeContainer.querySelector('#oversize-charge');
      // Multistore: the tier amounts are theme settings printed as data-tier-N on #oversize-info (copied onto
      // #oversize-quick-add by updateOversize). Without them, the former US constants.
      const tierInfo = (this.productEl && this.productEl.querySelector('#oversize-info[data-tier-1]'))
        || (this.oversizeContainer.hasAttribute('data-tier-1') ? this.oversizeContainer : null);
      const tier = (n, fallback) => (tierInfo ? tierInfo.getAttribute('data-tier-' + n) : fallback);
      const TIER_CHARGE = { 'Tier 1': tier(1, '10'), 'Tier 2': tier(2, '15'), 'Tier 3': tier(3, '5') };

      if (oversize && TIER_CHARGE[oversize]) {
        oversizeContainer.hidden = false;
        if (oversizeCharge) oversizeCharge.textContent = TIER_CHARGE[oversize];
      } else {
        oversizeContainer.hidden = true;
      }
    }

    /**
     * Opens the drawer and fetches the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    async open(opener) {
      opener.setAttribute('aria-disabled', 'true');

      if (this.notification) this.notification.hidden = true;

      // If it's the same product as previously shown, there's no need to re-fetch the details.
      if (this.productUrl && this.productUrl === opener.dataset.productUrl) {
        super.open(opener);
        opener.removeAttribute('aria-disabled');
        return;
      }

      this.productUrl = opener.dataset.productUrl;
      this.content.innerHTML = '';
      this.classList.add('is-loading');
      this.content.classList.add('drawer__content--out');
      this.footer.classList.add('drawer__footer--out');

      super.open(opener);

      if (!this.fetch || this.fetch.url !== opener.dataset.productUrl) {
        this.fetch = {
          url: opener.dataset.productUrl,
          promise: this.fetchProductHtml(opener.dataset.productUrl, opener.dataset.templateSuffix)
        };
      }

      try {
        const html = await this.fetch.promise;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        this.productEl = tmpl.content.querySelector('.cc-main-product .js-product');
        this.renderProduct(opener);
      } catch (error) {
        // A failed product request (also a failed hover prefetch) must not leave the drawer spinning: close it, forget
        // the product so the next click fetches again, and free the button (finally).
        this.productUrl = null;
        this.classList.remove('is-loading');
        this.close();
      } finally {
        this.fetch = null;
        opener.removeAttribute('aria-disabled');
      }
    }

    /**
     * Closes the cart drawer.
     */
    close() {
      super.close(() => {
        this.backBtn.hidden = true;
      });
    }

    /**
     * Renders the product details.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    renderProduct(opener) {
      // Replace instances of section id to prevent duplicates on the product page.
      const sectionId = this.productEl.dataset.section;
      this.productEl.innerHTML = this.productEl.innerHTML.replaceAll(sectionId, 'quickadd');

      // Prevent variant picker from updating the URL on change.
      const variantPicker = this.productEl.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.dataset.updateUrl = 'false';
        this.selectFirstVariant = variantPicker.dataset.selectFirstVariant === 'true';
      }

      // Remove size chart modal and link (if they exist).
      const sizeChartModal = this.productEl.querySelector('[data-modal="size-chart"]');
      if (sizeChartModal) {
        sizeChartModal.remove();
      }

      this.updateContent();
      this.updateForm();
      this.updateOversize();

      // Update the product media.
      const activeMedia = this.productEl.querySelector('.media-viewer__item.is-current-variant');
      if (activeMedia) this.updateMedia(activeMedia.dataset.mediaId);

      if (opener.dataset.selectedColor && this.selectFirstVariant) {
        // Timeout to allow the VariantPicker to initialize
        setTimeout(this.setActiveVariant.bind(this, opener), 10);
      }
    }

    /**
     * Set color variant to match the one selected in the card.
     * @param {Element} opener - Element that triggered opening of the drawer.
     */
    setActiveVariant(opener) {
      const colorOptionBox = this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`);
      if (colorOptionBox) {
        this.querySelector(`.opt-btn[value="${opener.dataset.selectedColor}"]`)
          .click();
      } else {
        const colorOptionDropdown = this.querySelector(
          `.custom-select__option[data-value="${opener.dataset.selectedColor}"]`
        );
        if (colorOptionDropdown) {
          const customSelect = colorOptionDropdown.closest('custom-select');
          customSelect.selectOption(colorOptionDropdown);
        }
      }
    }

    /**
     * Updates the product media.
     * @param {string} mediaId - Id of the media item to show.
     */
    updateMedia(mediaId) {
      if (!mediaId || !this.productEl) return;
      const img = this.productEl.querySelector(`[data-media-id="${mediaId}"] img`);
      if (!img) return;

      // Guarded because this is now called on EVERY variant change, not once on open. container was
      // dereferenced unchecked, and a lazy image can have neither .src nor .dataset.src yet - both
      // threw. A missing image should leave the previous one in place, never break the drawer.
      const rawSrc = img.src || img.dataset.src;
      if (!rawSrc) return;
      const src = rawSrc.split('&width=')[0];
      const container = this.querySelector('.quick-add-info__media');
      if (!container) return;
      const width = container.offsetWidth;
      if (!width) return;
      // naturalWidth/Height, because a just-swapped <img> can report width 0 before layout and that
      // would produce height: Infinity in the template below.
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      const aspectRatio = (w && h) ? w / h : 1;

      container.innerHTML = `
        <img src="${src}&width=${width}" srcset="${src}&width=${width}, ${src}&width=${width * 2} 2x" width="${width * 2}" height="${(width * 2) / aspectRatio}" alt="${img.alt}">
      `;
    }

    /**
     * Builds the markup for the drawer content element.
     */
    updateContent() {
      let weightElem = this.getElementHtml('.product-info__weight');
      if (weightElem && weightElem.length > 0) {
        weightElem = `<div class="product-info__weight text-sm mt-2">${weightElem}</div>`;
      }

      this.content.innerHTML = `
        <div class="quick-add-info grid mb-8">
          <div class="quick-add-info__media${theme.settings.blendProductImages ? ' image-blend' : ''}"></div>
          <div class="quick-add-info__details">
            <div class="product-vendor-sku mb-2 text-sm">
              ${this.getElementHtml('.product-vendor-sku')}
            </div>
            <div class="product-title">
              <a class="h6 js-prod-link" href="${this.productUrl}">
                ${this.getElementHtml('.product-title')}
              </a>
            </div>
            ${weightElem}
            <hr>
            <div class="product-price">
              ${this.getElementHtml('.product-price')}
            </div>
            <div class="text-theme-light text-sm mt-4">
              <a href="${this.productUrl}" class="link js-prod-link">
                ${theme.strings.viewDetails}
              </a>
            </div>
          </div>
          <div class="quick-add-info__details md:hidden"></div>
        </div>
        <div class="product-options">
          ${this.getElementHtml('.product-options', '.custom-option:not(.quick-add-hidden)')}
        </div>
        <div class="product-backorder">
          ${this.getElementHtml('.product-backorder')}
        </div>
      `;

      this.classList.remove('is-loading');
      this.content.classList.remove('drawer__content--out');
    }

     /**
     * Updates the Oversize information in the drawer.
     */
    updateOversize(){
      const oversizeContainer = this.productEl.querySelector('#oversize-info');
        if(oversizeContainer){
          this.oversizeContainer.innerHTML = oversizeContainer.innerHTML;
          // Multistore: the data-tier-N amounts travel with the markup.
          ['data-tier-1', 'data-tier-2', 'data-tier-3'].forEach((name) => {
            if (oversizeContainer.hasAttribute(name)) this.oversizeContainer.setAttribute(name, oversizeContainer.getAttribute(name));
          });
        }
    }

    /**
     * Updates the Quick Add drawer form (buy buttons).
     */
    updateForm() {
      const productForm = this.productEl.querySelector('product-form');
      this.footer.classList.remove('quick-add__footer-message');

      if (productForm) {
        this.form.innerHTML = productForm.innerHTML;
        this.form.init();

        if (Shopify && Shopify.PaymentButton) {
          Shopify.PaymentButton.init();
        }
      } else {
        const signUpForm = this.productEl.querySelector('.product-signup');
        if (signUpForm) {
          this.form.innerHTML = signUpForm.innerHTML;
        } else {
          this.footer.classList.add('quick-add__footer-message');
          this.form.innerHTML = `
            <div class="alert quick-add__alert bg-info-bg text-info-text">
              <div class="flex">
                <div>
                  <svg class="icon icon--price_tag" width="32" height="32" viewBox="0 0 16 16" aria-hidden="true" focusable="false" role="presentation">
                    <path fill="currentColor" d="M7.59 1.34a1 1 0 01.7-.29h5.66a1 1 0 011 1v5.66a1 1 0 01-.3.7L7.6 15.5a1 1 0 01-1.42 0L.52 9.83a1 1 0 010-1.42l7.07-7.07zm6.36 6.37l-7.07 7.07-5.66-5.66L8.3 2.05h5.66v5.66z" fill-rule="evenodd"/>
                    <path fill="currentColor" d="M9.7 6.3a1 1 0 101.42-1.42 1 1 0 00-1.41 1.41zM9 7a2 2 0 102.83-2.83A2 2 0 009 7z" fill-rule="evenodd"/>
                  </svg>
                </div>
                <div>
                  <p class="text-h6 font-bold mb-2">${theme.strings.awaitingSale}</p>
                  <a class="link js-prod-link" href="${this.productUrl}">${theme.strings.viewDetails}</a>
                </div>
              </div>
            </div>`;
        }
      }

      this.footer.classList.remove('drawer__footer--out');
    }

    /**
     * Gets the innerHTML of elements within the product element.
     * @param {string} selector - CSS selector for the element.
     * @param {...*} additionalSelectors - Additional CSS selectors for elements.
     * @returns {?string}
     */
    getElementHtml(selector, ...additionalSelectors) {
      const selectors = [selector].concat(additionalSelectors);
      let html = '';
      selectors.forEach((sel) => {
        const els = this.productEl.querySelectorAll(sel);
        Array.from(els).forEach((el) => {
          html += el.innerHTML;
        });
      });
      return html;
    }

    /**
     * Shows an "Added to cart" message in the drawer.
     */
    addedToCart() {
      if (this.notification) {
        setTimeout(() => {
          this.notification.hidden = false;
        }, 300);

        setTimeout(() => {
          this.notification.hidden = true;
        }, this.notification.dataset.visibleFor);
      }
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
